import { useSearchParams } from "#routers/hooks";
import { EstimationMethodListTablePanel } from "#routes/calculations/estimation-methods/EstimationMethodList/EstimationMethodListTablePanel";
import { ImportDataActionStatusWithQueue } from "#src/batteries-included-components/Banners/ImportDataActionStatus";
import { getImportDataActionQueueKey } from "#src/batteries-included-components/Buttons/ImportDataAction/ImportDataAction.helpers";
import { EstimationMethodDetailViewFilterArea } from "#src/batteries-included-components/FilterAreas/EstimationMethodsFilterAreas/EstimationMethodsFilterAreas";
import { useIsFeatureAvailable } from "#src/contexts/AuthenticatedContext.helpers";
import useLocalization from "#src/hooks/useLocalization";
import { useStorageKey } from "#src/hooks/useStorageKey";
import { useBreadcrumbsFromRoute } from "#src/utils/route";
import { Page, Tab } from "@validereinc/common-components";
import { AssetType, ResourceDefinitions } from "@validereinc/domain";
import React from "react";
import { CalculationsEstimationMethodsRoutePath } from ".";

const DEFAULT_TAB_KEY = "equipment";
const importDataActionQueueKey = getImportDataActionQueueKey({
  resource: ResourceDefinitions.estimation_method,
});

export const EstimationMethodListPage = () => {
  const { localize } = useLocalization();
  const [
    equipmentFilterConfigStorageKey,
    equipmentTableConfigStorageKey,
    equipmentViewConfigStorageKey,
    equipmentTableStateStorageKey,
  ] = useStorageKey(
    [
      "filter-configuration",
      "table-configuration",
      "view-configuration",
      "table-state",
    ],
    ["calculations", "estimation-methods", "list", "equipment"]
  );
  const [
    flowFilterConfigStorageKey,
    flowTableConfigStorageKey,
    flowViewConfigStorageKey,
    flowTableStateStorageKey,
  ] = useStorageKey(
    [
      "filter-configuration",
      "table-configuration",
      "view-configuration",
      "table-state",
    ],
    ["calculations", "estimation-methods", "list", "flows"]
  );
  const [{ tab }, setSearchParams] = useSearchParams();
  const [isDataIngestionEnabled, isLoadingIsDataIngestionEnabled] =
    useIsFeatureAvailable({
      featureFlagQuery: "core:data_pipeline",
    });
  const [areEstimationMethodsEnabled, isLoadingAreEstimationMethodsEnabled] =
    useIsFeatureAvailable({
      featureFlagQuery: "core:volumetric_estimation_methods",
    });
  const [breadcrumbs] = useBreadcrumbsFromRoute(
    CalculationsEstimationMethodsRoutePath
  );

  return (
    <Page
      title={CalculationsEstimationMethodsRoutePath.title}
      category={CalculationsEstimationMethodsRoutePath.previous?.title}
      breadcrumbs={breadcrumbs}
      activeTabKey={tab ?? DEFAULT_TAB_KEY}
      onActiveTabKeyChange={(tab) => setSearchParams({ tab })}
      isLoading={
        isLoadingIsDataIngestionEnabled || isLoadingAreEstimationMethodsEnabled
      }
    >
      <Tab
        title="Equipment"
        tabKey="equipment"
      >
        {isDataIngestionEnabled ? (
          <ImportDataActionStatusWithQueue
            queueKey={importDataActionQueueKey}
          />
        ) : null}
        <EstimationMethodDetailViewFilterArea
          viewConfigStorageKey={equipmentViewConfigStorageKey}
        />
        <EstimationMethodListTablePanel
          filterConfigStorageKey={equipmentFilterConfigStorageKey}
          tableConfigStorageKey={equipmentTableConfigStorageKey}
          tableStateStorageKey={equipmentTableStateStorageKey}
          viewConfigStorageKey={equipmentViewConfigStorageKey}
          assetType={AssetType.EQUIPMENT}
        />
      </Tab>
      {areEstimationMethodsEnabled ? (
        <Tab
          title={`${localize("Flow")}`}
          tabKey="flow"
        >
          {isDataIngestionEnabled ? (
            <ImportDataActionStatusWithQueue
              // @ts-expect-error invalid props
              resource={ResourceDefinitions.estimation_method}
            />
          ) : null}
          <EstimationMethodDetailViewFilterArea
            viewConfigStorageKey={flowViewConfigStorageKey}
          />
          <EstimationMethodListTablePanel
            filterConfigStorageKey={flowFilterConfigStorageKey}
            tableConfigStorageKey={flowTableConfigStorageKey}
            tableStateStorageKey={flowTableStateStorageKey}
            viewConfigStorageKey={flowViewConfigStorageKey}
            assetType={AssetType.FLOW}
          />
        </Tab>
      ) : null}
    </Page>
  );
};
